import {
  reactive,
  createApp,
  Component,
  FunctionalComponent,
  ComponentOptions,
  h,
} from "vue";

type ClassComponent<T> = {
  new (...args: any[]): T;
};

type GetComponentProps<Comp extends Component> =
  Comp extends ClassComponent<any>
    ? InstanceType<Comp>["$props"]
    : Comp extends FunctionalComponent
    ? ReturnType<Comp>["props"]
    : unknown;

export class ApiModal<DefineComponent extends Component> {
  instance: ReturnType<ReturnType<typeof createApp>["mount"]> | null = null;
  private $app: ReturnType<typeof createApp> | null = null;
  private $root: HTMLElement | null = null;
  private $state = reactive({ show: false });
  private $props = {} as GetComponentProps<DefineComponent>;
  private $events = {} as Record<string, Function>;
  private $rootComponent?: DefineComponent;
  private $rootProps?: GetComponentProps<DefineComponent> | null;
  private $options: {
    /** 是否同步执行 */
    sync?: boolean;
  } = {};

  constructor(
    rootComponent: DefineComponent,
    rootProps?: GetComponentProps<typeof rootComponent> | null,
    options?: {
      /** 是否同步执行 */
      sync?: boolean;
    },
  ) {
    this.$rootComponent = rootComponent;
    this.$rootProps = rootProps;
    this.$options = options ?? {};

    this.create();

    window.addEventListener("beforeunload", () => {
      this.unmount();
    });
  }

  private create() {
    const { sync = true } = this.$options ?? {};
    const rootProps = this.$rootProps;
    const rootComponent = this.$rootComponent!;
    this.setInnerProps(rootProps);
    const app = createApp(
      this.wrapperComponent(rootComponent, rootProps as any),
    );
    const creator = () => {
      const root = document?.createElement?.("div");
      document?.body?.appendChild?.(root);
      this.instance = app.mount(root);
      this.$app = app;
      this.$root = root;
    };
    if (sync) {
      creator();
    } else {
      nextTick(() => {
        creator();
      });
    }
  }

  private setInnerProps(props?: GetComponentProps<DefineComponent> | null) {
    if (!props) return;
    this.$props = props!;
    if (props?.show !== undefined) this.$state.show = !!(props as any)?.show;
    if (typeof props?.["onUpdate:show"] === "function") {
      this.$events["onUpdate:show"] = props["onUpdate:show"];
    }
  }

  private wrapperComponent(
    component: DefineComponent,
    props: GetComponentProps<typeof component> | null,
  ) {
    const self = this;
    const handleUpdateShow = self.handleUpdateShow.bind(this);
    const Wrapper = {
      setup() {
        return () =>
          h(component, {
            ...props,
            ...self.$props,
            show: self.$state.show,
            "onUpdate:show": handleUpdateShow,
          });
      },
    } as ComponentOptions;

    return Wrapper;
  }

  private handleUpdateShow(show: boolean) {
    this.$state.show = !!show;
    this.$events?.["onUpdate:show"]?.(show);
  }

  /** 显示 */
  show(props?: GetComponentProps<DefineComponent>) {
    if (!this.$root) {
      this.create();
    }
    this.setInnerProps(props);
    this.toggle(true);
  }

  /** 切换显隐 */
  toggle(show?: boolean) {
    this.instance?.$nextTick(() => {
      this.$state.show = show ?? !this.$state.show;
    });
  }

  /** 关闭 */
  close() {
    this.toggle(false);
  }

  unmount() {
    this.$app?.unmount();
    if (this.$root) document?.body?.removeChild(this.$root);
    this.$root = null;
  }
  /** 销毁实例 */
  destroy() {
    this.unmount();
  }
}
